const post = {
  slug: 's3-is-a-database-a-cdn-and-a-footgun',
  number: 19,
  title: 'S3 Is a Database, a CDN, and a Footgun',
  date: '2025-09-15',
  readTime: '6 min read',
  views: 36,
  category: 'aws',
  tags: ['AWS', 'S3', 'Storage'],
  excerpt:
    'S3 looks like a place to put files. It is really one of the most flexible building blocks in AWS, and one of the easiest to misuse. A field guide to using it well, from someone who has misused it.',
  cover: null,
  content: `
On the surface, S3 is a place to put files, and that description sells it badly short. S3 is one of the most flexible and reliable building blocks in all of AWS. People use it as a data lake, a static website host, a backup target, and the quiet glue between dozens of services. It is also remarkably easy to misuse in ways that cost money or leak data, which is how it earns the footgun half of its reputation. I have made a couple of those mistakes myself, so consider this the field guide I wish I had read first.

## It is object storage, not a filesystem

The first thing to internalize is that S3 is not a hard drive with folders. It stores objects identified by a key, and the slashes in those keys that look like folders are just part of the name. There is no real directory structure underneath, no matter how much the console dresses it up to look like one.

This matters because it changes how you think about access. You do not list a folder cheaply the way you would on a filesystem, and naming your keys thoughtfully, so the ones you fetch together share sensible prefixes, makes a real difference at scale. Treating S3 like a filesystem works right up until it suddenly does not, usually when the data gets large.

## The defaults are safe, your changes are the risk

Most of the S3 data leaks you read about are not failures of S3. They are someone making a bucket public to solve an access problem quickly and then forgetting about it. AWS has steadily made the safe choice the default, with public access blocked unless you go out of your way to allow it.

The lesson is to respect that default and find another way to grant access. If something outside AWS needs a file, hand out a temporary signed link to that specific object rather than opening the entire bucket to the world.

\`\`\`text
Risky:  make the bucket public so an external service can read one file
Safe:   keep the bucket private, hand out a short-lived signed URL per object
\`\`\`

The moment you catch yourself making a bucket public to move faster, stop, because that exact shortcut is the one that ends up in an incident report with your name on the commit.

## Lifecycle rules are free money

Data accumulates in S3 forever unless you tell it not to. Logs, backups, old exports, all of it quietly piling up and quietly billing you. Most of that old data is rarely accessed, and paying top-tier storage prices to keep it is simply waste you have not noticed yet.

Lifecycle rules fix this automatically. You set a policy that moves older objects to cheaper storage tiers, or deletes them entirely after a set time, and from then on the cleanup happens without anyone thinking about it.

\`\`\`text
objects older than 30 days  -> move to a cheaper, colder tier
objects older than 365 days -> delete
\`\`\`

Setting these once is one of the highest-return ten-minute tasks in all of AWS.

## Match the storage class to the access pattern

S3 is not one thing, it is a family of storage classes at different price points, traded off against how quickly and cheaply you can read the data back. Frequently accessed data belongs in the standard tier. Backups you hope to never read belong in a cold, cheap archive tier.

The mistake is leaving everything in the default class out of inertia. Knowing roughly how often each kind of data is actually read, and placing it accordingly, can cut storage costs dramatically with no downside, because you are simply paying for the access pattern you genuinely have instead of the most expensive one by default.

## A checklist

- Think in keys and prefixes, not folders, and name keys for how you read them.
- Keep buckets private, share specific objects with short-lived signed URLs.
- Put lifecycle rules on anything that accumulates: logs, backups, exports.
- Match storage classes to real access frequency instead of defaulting everything to standard.

S3 is a quiet powerhouse, the dependable foundation half your architecture can sit on. Treat it as object storage, keep it private by default, let old data age out on its own, and pay for the access pattern you actually have. Do that, and the footgun everyone warns about stays firmly pointed away from your feet.
`,
};

export default post;
